import { useNavigate } from "react-router-dom";
import { TextField, Button, IconButton, InputAdornment } from "@mui/material";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import React from "react";
import { useChangePassword } from "../../api/hooks/useAuth";
import { showSuccess, showError } from "../../helpers/toast";
import { isStrongPassword } from "../../helpers/validations";

const ChangePassword = () => {
  const navigate = useNavigate();

  const [oldPassword, setOldPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [passwordErrors, setPasswordErrors] = React.useState({
    old: "",
    new: "",
    confirm: "",
  });

  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const [showPassword1, setShowPassword1] = React.useState(false);
  const handleClickShowPassword1 = () => {
    setShowPassword1((prev) => !prev);
  };
  const handleMouseDownPassword1 = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const [showPassword2, setShowPassword2] = React.useState(false);
  const handleClickShowPassword2 = () => {
    setShowPassword2((prev) => !prev);
  };
  const handleMouseDownPassword2 = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const { mutate: changePassword, isPending: isLoading } = useChangePassword({
    onSuccess: (response) => {
      showSuccess(response.message || "Password changed successfully");
      // Clear form
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordErrors({ old: "", new: "", confirm: "" });
      // Optionally redirect to dashboard or stay on page
      // navigate("/dashboard");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to change password. Please try again.";
      showError(errorMessage);
    },
  });

  const validateForm = (): boolean => {
    const errors = { old: "", new: "", confirm: "" };
    let isValid = true;

    if (!oldPassword) {
      errors.old = "Old password is required";
      isValid = false;
    }

    if (!newPassword) {
      errors.new = "New password is required";
      isValid = false;
    } else if (!isStrongPassword(newPassword)) {
      errors.new = "Min 8 chars, 1 uppercase, 1 special character required";
      isValid = false;
    }

    if (!confirmPassword) {
      errors.confirm = "Please confirm your password";
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      errors.confirm = "Passwords do not match";
      isValid = false;
    }

    if (oldPassword && newPassword && oldPassword === newPassword) {
      errors.new = "New password must be different from old password";
      isValid = false;
    }

    setPasswordErrors(errors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      changePassword({
        oldPassword: oldPassword,
        newPassword: newPassword,
      });
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>Change Password</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button>
          {' '} - <button onClick={() => navigate("/manage-profile")}>Manage Profile</button>
          {' '} - Change Password
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <form onSubmit={handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_33">
                <label htmlFor="password">Old Password</label>
                <TextField
                  id="password"
                  placeholder="Enter Old Password"
                  fullWidth
                  hiddenLabel
                  type={showPassword ? "text" : "password"}
                  value={oldPassword}
                  onChange={(e) => {
                    setOldPassword(e.target.value);
                    if (passwordErrors.old) setPasswordErrors({ ...passwordErrors, old: "" });
                  }}
                  error={!!passwordErrors.old}
                  helperText={passwordErrors.old}
                  required
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" className="eye_btn">
                        <IconButton
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              <div className="control_group w_33">
                <label htmlFor="newpassword">New Password</label>
                <TextField
                  id="newpassword"
                  placeholder="Enter New Password"
                  hiddenLabel
                  fullWidth
                  type={showPassword1 ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (passwordErrors.new) setPasswordErrors({ ...passwordErrors, new: "" });
                    // Clear confirm error if passwords now match
                    if (passwordErrors.confirm && e.target.value === confirmPassword) {
                      setPasswordErrors({ ...passwordErrors, new: "", confirm: "" });
                    }
                  }}
                  error={!!passwordErrors.new}
                  helperText={passwordErrors.new}
                  required
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" className="eye_btn">
                        <IconButton
                          aria-label={
                            showPassword1 ? "Hide password" : "Show password"
                          }
                          onClick={handleClickShowPassword1}
                          onMouseDown={handleMouseDownPassword1}
                          edge="end"
                        >
                          {showPassword1 ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              <div className="control_group w_33">
                <label htmlFor="confirmpassword">Confirm New Password</label>
                <TextField
                  id="confirmpassword"
                  placeholder="Enter Confirm New Password"
                  hiddenLabel
                  fullWidth
                  type={showPassword2 ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (passwordErrors.confirm) {
                      if (e.target.value === newPassword) {
                        setPasswordErrors({ ...passwordErrors, confirm: "" });
                      } else {
                        setPasswordErrors({ ...passwordErrors, confirm: "Passwords do not match" });
                      }
                    }
                  }}
                  error={!!passwordErrors.confirm}
                  helperText={passwordErrors.confirm}
                  required
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" className="eye_btn">
                        <IconButton
                          aria-label={
                            showPassword2 ? "Hide password" : "Show password"
                          }
                          onClick={handleClickShowPassword2}
                          onMouseDown={handleMouseDownPassword2}
                          edge="end"
                        >
                          {showPassword2 ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </div>
            <div className="form_btn">
              <Button
                type="submit"
                className="btn btn_primary"
                variant="contained"
                disabled={isLoading}
              >
                {isLoading ? "Changing..." : "Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ChangePassword;
